/**
 * Retail V1 navigation engine — Phase 2 §2.2.
 * Pure decision module: no React, no feature imports (PG-025).
 */
import { readLastKnownSalesPointSlug } from '../shared/session/lastKnownSalesPoint.js';
import type {
  BackNavigationDecision,
  CatchAllRedirectDecision,
  EntryPointInput,
  JourneyId,
  JourneyResolution,
  MasterScreenId,
  RecoveryClass,
  RecoveryDecision,
  RouteContext,
} from './navigationEngine.types.js';

export type {
  BackNavigationDecision,
  CatchAllRedirectAction,
  CatchAllRedirectDecision,
  EntryPointInput,
  EntryPointType,
  JourneyId,
  JourneyResolution,
  MasterScreenId,
  NavigationShell,
  RecoveryClass,
  RecoveryDecision,
  RecoveryNavigationMode,
  RouteContext,
  RouteContextKey,
} from './navigationEngine.types.js';

const ENTRY_JOURNEY_MAP: Readonly<
  Record<EntryPointInput['entryType'], { journeyId: JourneyId; shell: JourneyResolution['shell']; journeyContextKind: JourneyResolution['journeyContextKind'] }>
> = {
  QR: { journeyId: 'J-SHOP-PURCHASE', shell: 'JourneyShell', journeyContextKind: 'PHONE_FIRST_PRODUCT' },
  'Deep Link': { journeyId: 'J-PHONE-FIRST-PRODUCT', shell: 'JourneyShell', journeyContextKind: 'PHONE_FIRST_PRODUCT' },
  'Tenant Link': { journeyId: 'J-TENANT-SELECT', shell: 'CustomerAppShell', journeyContextKind: null },
  'Receipt Link': { journeyId: 'J-ACCOUNT-RELATIONSHIP', shell: 'CustomerAppShell', journeyContextKind: null },
  'Payment Link': { journeyId: 'J-PAYMENT', shell: 'CustomerAppShell', journeyContextKind: null },
  'Notification Link': { journeyId: 'J-SHARED', shell: 'CustomerAppShell', journeyContextKind: null },
  'Returning Session': { journeyId: 'J-SHOP-PURCHASE', shell: 'CustomerAppShell', journeyContextKind: null },
  'Manual Login': { journeyId: 'J-AUTH', shell: 'JourneyShell', journeyContextKind: null },
  'Handoff Token': { journeyId: 'J-PHONE-FIRST-PRODUCT', shell: 'JourneyShell', journeyContextKind: 'PHONE_FIRST_PRODUCT' },
  'Post-Kiosk Token': { journeyId: 'J-POST-KIOSK-PRODUCT', shell: 'JourneyShell', journeyContextKind: 'POST_KIOSK_PRODUCT' },
  'Pickup Verify Token': { journeyId: 'J-PICKUP-VERIFY', shell: 'JourneyShell', journeyContextKind: 'PHONE_FIRST_PRODUCT' },
  'Card Link': { journeyId: 'J-CARD-LINK', shell: 'JourneyShell', journeyContextKind: null },
};

/** Auth screens that block browser back into the journey (exact registry ids). */
const AUTH_BLOCKED_MS_IDS: ReadonlySet<MasterScreenId> = new Set([
  'MS-AUTH-010',
  'MS-AUTH-011',
  'MS-AUTH-012',
  'MS-AUTH-013',
  'MS-AUTH-014',
  'MS-AUTH-015',
  'MS-AUTH-016',
  'MS-AUTH-017',
]);

/** Account / hub / order / consent shells that allow hierarchical named-parent back. */
const ACCOUNT_SHELL_MS_IDS: ReadonlySet<MasterScreenId> = new Set([
  'MS-ORD-050',
  'MS-ORD-051',
  'MS-ORD-052',
  'MS-ORD-053',
  'MS-ORD-054',
  'MS-ORD-055',
  'MS-ACC-060',
  'MS-ACC-061',
  'MS-ACC-062',
  'MS-ACC-063',
  'MS-ACC-064',
  'MS-ACC-065',
  'MS-ACC-066',
  'MS-ACC-067',
  'MS-ACC-068',
  'MS-ACC-069',
  'MS-ACC-070',
  'MS-ACC-071',
  'MS-HUB-020',
  'MS-HUB-021',
  'MS-HUB-022',
  'MS-HUB-023',
  'MS-HUB-024',
  'MS-HUB-025',
  'MS-HUB-026',
  'MS-HUB-027',
  'MS-CON-080',
  'MS-CON-081',
  'MS-CON-082',
]);

export function createEmptyRouteContext(tenantCode: string): RouteContext {
  return {
    tenantCode,
    salesPointId: null,
    salesPointSlug: null,
    journeyId: null,
    checkoutSessionId: null,
    customerId: null,
    paymentActive: false,
    pickupToken: null,
    receiptRef: null,
    notificationTarget: null,
  };
}

export function resolveJourneyFromEntry(entry: EntryPointInput): JourneyResolution {
  const mapped = ENTRY_JOURNEY_MAP[entry.entryType];
  return {
    journeyId: mapped.journeyId,
    shell: mapped.shell,
    journeyContextKind: mapped.journeyContextKind,
  };
}

export function resolveAllowedBackNavigation(
  journey: JourneyId,
  currentMsId: MasterScreenId,
): BackNavigationDecision {
  // UI chrome uses resolveHierarchicalBackTarget (named parents). This decision
  // remains for journey gatekeeping (auth / payment-locked steps).
  if (journey === 'J-AUTH') {
    return 'blocked';
  }

  if (AUTH_BLOCKED_MS_IDS.has(currentMsId)) {
    return 'blocked';
  }

  // Exact MS ids — avoid startsWith matching future siblings (e.g. MS-COM-031x).
  if (currentMsId === 'MS-COM-031' || currentMsId === 'MS-COM-036') {
    return 'blocked';
  }

  if (ACCOUNT_SHELL_MS_IDS.has(currentMsId)) {
    return 'allowed';
  }

  if (journey === 'J-PHONE-FIRST-PRODUCT' || journey === 'J-PHONE-FIRST-DONATION') {
    return 'exit-journey';
  }

  return 'allowed';
}

export function preserveContextsOnTransition(
  from: RouteContext,
  to: Partial<RouteContext>,
): RouteContext {
  const nextTenant = to.tenantCode ?? from.tenantCode;
  const tenantSwitch = nextTenant !== from.tenantCode;

  if (tenantSwitch) {
    return {
      tenantCode: nextTenant,
      salesPointId: to.salesPointId ?? null,
      salesPointSlug: to.salesPointSlug ?? null,
      journeyId: to.journeyId ?? null,
      checkoutSessionId: to.checkoutSessionId ?? null,
      customerId: to.customerId ?? from.customerId,
      paymentActive: to.paymentActive ?? false,
      pickupToken: to.pickupToken ?? null,
      receiptRef: to.receiptRef ?? null,
      notificationTarget: to.notificationTarget ?? null,
    };
  }

  return {
    ...from,
    ...to,
    tenantCode: nextTenant,
  };
}

export function resolveRecoveryNavigation(
  recoveryClass: RecoveryClass,
  ctx: RouteContext,
): RecoveryDecision {
  const tenantBase = `/${encodeURIComponent(ctx.tenantCode)}`;

  switch (recoveryClass) {
    case 'unavailable':
      return {
        mode: 'redirect-unavailable',
        targetPath: `${tenantBase}/unavailable`,
      };
    case 'access-denied':
      return {
        mode: 'recovery-access-denied',
        targetPath: `${tenantBase}/recovery/access-denied`,
      };
    case 'session-expired':
      return {
        mode: 'recovery-session-expired',
        targetPath: `${tenantBase}/recovery/session-expired`,
      };
    case 'return-target':
      return {
        mode: 'navigate',
        targetPath: resolvePostAuthFallbackPath(ctx.tenantCode),
      };
  }
}

export function resolvePostAuthFallbackPath(tenantCode: string): string {
  /* Chrome sentinel — never a real org shop URL. */
  if (tenantCode === '__platform__') {
    return '/';
  }
  const rememberedSlug = readLastKnownSalesPointSlug(tenantCode);
  if (rememberedSlug !== null) {
    return `/${encodeURIComponent(tenantCode)}/${encodeURIComponent(rememberedSlug)}`;
  }
  // Bare `/{tenant}/shop` is kiosk-bound (post-kiosk / ?kioskId). Organic exit → tenant shops.
  return `/${encodeURIComponent(tenantCode)}/shops`;
}

export function resolveCatchAllRedirectDecision(
  pathname: string,
  tenantCode: string,
): CatchAllRedirectDecision {
  const encodedTenant = encodeURIComponent(tenantCode);
  const legacyTenantSignInPrefix = `/${encodedTenant}/sign-in`;
  // G3 — canonical login is path-tenant `/{tenant}/sign-in` (never `/sign-in?tenant=`).
  const canonicalSignIn = `/${encodedTenant}/sign-in`;
  const tenantShops = `/${encodedTenant}/shops`;

  if (
    pathname.startsWith(`${legacyTenantSignInPrefix}/`) ||
    pathname.includes('/sign-in/sign-in')
  ) {
    return { action: 'replace-canonical-sign-in', target: canonicalSignIn };
  }

  // Unknown /{tenant}/… → org shops directory (keep tenant context).
  return { action: 'navigate-tenant-shops', target: tenantShops };
}

export function buildHandoffCheckoutPath(
  tenantCode: string,
  checkoutSessionId: string,
): string {
  return `/${encodeURIComponent(tenantCode)}/checkout?sessionId=${encodeURIComponent(checkoutSessionId)}`;
}
